import {Injectable} from "@angular/core";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../redux-store";
import {Router} from "@angular/router";
import {UserService} from "../../crm/user/user.service";
import {Observable} from "rxjs/Observable";
import {IUser, MessageType} from "../redux-store/session/session.types";
import {Apollo} from "apollo-angular";
import {environment} from "../../../environments/environment";
import {isUndefined} from "util";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {catchError, delay, map, mergeMap, retryWhen, scan, tap} from "rxjs/operators";
import {empty} from "rxjs/observable/empty";
import {VisBOSMessage} from "../../core/visbos-core/ui/messages/VisBOSMessage.interface";

@Injectable()
export class SessionActions {
  static LOGIN_USER = 'LOGIN_USER';
  static LOGIN_USER_SUCCESS = 'LOGIN_USER_SUCCESS';
  static LOGIN_USER_ERROR = 'LOGIN_USER_ERROR';
  static LOGOUT_USER = 'LOGOUT_USER';
  static REFRESH_TOKEN = 'REFRESH_TOKEN';

  static INIT_ASYNC_ACTION = 'INIT_ASYNC_ACTION';
  static FINISH_ASYNC_ACTION = 'FINISH_ASYNC_ACTION';
  static SET_REDIRECT_URL = 'SET_REDIRECT_URL';

  static PUSH_MESSAGE = 'PUSH_MESSAGE';
  static REMOVE_MESSAGE = 'REMOVE_MESSAGE';
  static CLEAR_ALL_MESSAGES = 'CLEAR_ALL_MESSAGES';

  static THROW_ERROR = 'THROW_ERROR';

  growlMsgs: BehaviorSubject<VisBOSMessage[]> = new BehaviorSubject([]);
  private growlTimeout: any;

  constructor(private ngRedux: NgRedux<IAppState>,
              private apollo: Apollo,
              private userService: UserService,
              private http: HttpClient,
              private router: Router) {
  }

  public throwError(err, userMessage?: string, issuer?: string, hideErrMessage: boolean = false) {

    let msg = userMessage ? userMessage : 'Es ist ein unerwarteter Fehler aufgetreten!';

    if (err && err.message && !hideErrMessage) {
      msg = `<div>${msg}</div><i>${err.message}</i>`;
    }

    this.pushMessage(msg, MessageType.ERROR, issuer || 'VisBOS');
    this.finishAsyncAction();
    throw err;
  }

  public initAsyncAction(actionLabel?) {
    this.ngRedux.dispatch({
      type: SessionActions.INIT_ASYNC_ACTION,
      payload: actionLabel
    })
  }

  public finishAsyncAction(actionLabel?) {
    this.ngRedux.dispatch({
      type: SessionActions.FINISH_ASYNC_ACTION,
      payload: actionLabel
    })
  }

  public refreshToken(newToken: string) {
    if (!newToken) {
      console.warn("Refreshing token to null!", [newToken]);
    }

    this.ngRedux.dispatch({
      type: SessionActions.REFRESH_TOKEN,
      payload: newToken
    });
  }

  public loginUser(email: string, password: string): Observable<IUser> {

    // Dispatch login action
    this.ngRedux.dispatch({
      type: SessionActions.LOGIN_USER,
      payload: {email: email, password: password},
    });

    let headers = new HttpHeaders(
      {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      });
    let options = {'headers': headers};

    // Login user via REST
    return this.http
      .post(
        environment.authServer,
        JSON.stringify({email, password}),
        options
      ).pipe(
        retryWhen(errors => errors.pipe(
          tap(e => {
              if (e.status == 404) {
                throw e;
              }
            }
          ),
          scan<number>((errorCount, err) => {
            if (errorCount >= 5) {
              throw err;
            }
            return errorCount + 1;
          }, 0),
          delay(500)
          )
        ),
        catchError(error => {
          this.ngRedux.dispatch({
            type: SessionActions.LOGIN_USER_ERROR,
          });

          this.pushMessage("Benutzername oder Passwort falsch!", MessageType.ERROR, "Log-In");

          return empty();
        }),
        mergeMap((res: any) => {
          let token = res.token || null;
          this.refreshToken(token);
          return this.fetchAuthorizedUser();
        }),
        map<any, IUser>(userRes => {
          this.ngRedux.dispatch({
            type: SessionActions.LOGIN_USER_SUCCESS,
            payload: {profile: userRes.user},
          });

          return userRes;
        })
      );


  };

  public fetchAuthorizedUser(token?: string): Observable<any> {

    // Get token from parameter or from store. If both fails, logout user
    if (!(token = !token ? this.ngRedux.getState().session.token : token)) {
      this.logoutUser();
      return empty();
    }

    const obs = this.userService.getUser(token)
      .pipe(tap(userRes => {
        this.ngRedux.dispatch({
          type: SessionActions.LOGIN_USER_SUCCESS,
          payload: {profile: userRes.user},
        });
      }));

    return obs;
  }


  public logoutUser() {
    this.router.navigate(['logout']);
  }

  public setRedirectUrl(url: string) {
    this.ngRedux.dispatch({
      type: SessionActions.SET_REDIRECT_URL,
      payload: url,
    });
  }

  private getGrowlSeverity(type: MessageType): string {
    switch (type) {
      case MessageType.INFO:
        return 'info';
      case MessageType.SUCCESS:
        return 'success';
      case MessageType.WARNING:
        return 'warn';
      case MessageType.ERROR:
        return 'error';
      default:
        return 'info';
    }
  }

  public pushMessage(message: string, type: MessageType = MessageType.INFO, issuer?: string, persist?: boolean, sticky?: boolean) {

    let newMsg: VisBOSMessage = {
      severity: this.getGrowlSeverity(type),
      summary: issuer ? issuer : null,
      detail: message,
      sticky: sticky || false
    };

    this.growlMsgs.next([...this.growlMsgs.value, newMsg]);

    this.handleGrowlChange();

    if ((type == MessageType.ERROR && isUndefined(persist)) || persist == true) {
      this.ngRedux.dispatch({
        type: SessionActions.PUSH_MESSAGE,
        payload: {
          message: message,
          type: type
        }
      });
    }
  }

  public clearAllMessages() {

    this.growlMsgs.next([]);

    this.ngRedux.dispatch({type: SessionActions.CLEAR_ALL_MESSAGES})
  }

  public removeMessage(index: number) {

    let newMsgs = [...this.growlMsgs.value];
    newMsgs.splice(index, 1);
    this.growlMsgs.next(newMsgs);

    this.ngRedux.dispatch({
      type: SessionActions.REMOVE_MESSAGE,
      payload: index
    })
  }

  handleGrowlChange() {
    if (this.growlTimeout) {
      clearTimeout(this.growlTimeout);
    }

    this.growlTimeout = setTimeout(() => {
      let newMsgs = [...this.growlMsgs.value];
      newMsgs = newMsgs.filter(m => m.sticky);
      this.growlMsgs.next(newMsgs);
    }, 5000);
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/actions/session.actions.ts